import { OmitField } from '../../decorators/api-field.decorator';
import { ModelBase } from '../abstract/model-base.model';
import { Account } from '../accounting/account.model';
import { Address } from '../address/address.model';
import { Country } from '../core/country.model';
import { Currency } from '../core/currency.model';
import { PartnerContact } from './contact.model';
import { PartnerGroup } from './partner-group.model';
import { PartnerIdentifier } from './partner-identifier.model';
import { PartnerSalesSettings } from './partner-sales-settings.model';
import { PartnerType } from './partner-type.model';
import { TaxDetail } from '../core/taxDetail.model';
import { PaymentTerm } from '../invoicing/payment-term.model';
import { Organization } from '../legal-entity/organization.model';
import { Subscription } from '../subscription/subscription.model';
import { PersonNameOrderEnum } from '../../enums/person-name-order.enum';

export enum PartnerModeEnum {
    Client = 'client',
    Grantor = 'grantor',
    Vendor = 'vendor',
}
export type PartnerModes = PartnerModeEnum.Client | PartnerModeEnum.Grantor | PartnerModeEnum.Vendor;

export class Partner extends ModelBase {

    static override CLASS_NAME: string = 'Partner';

    /**
     * Dénomination d'un particulier composée du nom et du prénom, dans l'ordre du réglage
     * `partnerNameOrder` du dossier (#1087) — « Nom Prénom » à défaut. Une seule information
     * saisie ne laisse ni séparateur ni espace en trop.
     */
    public static composeIndividualName(lastName: string | null, firstName: string | null, order?: string): string {
        const parts = order === PersonNameOrderEnum.FirstLast ? [firstName, lastName] : [lastName, firstName];
        return parts.filter(part => !!part).join(' ').trim();
    }

    private _name!: string;
    private _type!: PartnerType;
    private _country!: Country;
    private _organization!: Organization;
    // Client data
    private _autoProductAccount!: boolean;
    private _isClient!: boolean;
    private _clientGroup!: PartnerGroup;
    private _clientNumber!: string;
    private _myVendorNumber!: string;
    private _clientAccount!: Account;
    private _clientAccountSuggestions!: Account[];
    private _clientCreditorAccount!: Account;
    private _clientBalance!: number;
    private _clientInitialBalance!: number;
    private _clientRemainingInitialBalance!: number;
    private _productAccount!: Account;
    private _productAccountSuggestions!: Account[];
    private _productAccountSuggestionDismissed!: boolean;
    // Réglages de vente propres au client, en cascade sur ceux du dossier (#1114)
    private _salesSettings?: PartnerSalesSettings;
    // Vendor data
    private _autoChargeAccount!: boolean;
    private _isVendor!: boolean;
    private _vendorGroup!: PartnerGroup;
    private _vendorNumber!: string;
    private _vendorAccount!: Account;
    private _vendorAccountSuggestions!: Account[];
    private _vendorDebtorAccount!: Account;
    private _vendorBalance!: number;
    private _vendorInitialBalance!: number;
    private _vendorRemainingInitialBalance!: number;
    private _chargeAccount!: Account;
    private _vendorPaymentTerm?: PaymentTerm;
    private _vendorCurrency!: Currency;
    private _vendorInvoiceAutoValidation!: boolean;
    private _chargeAccountSuggestions!: Account[];
    private _chargeAccountSuggestionDismissed!: boolean;
    // Grantor data
    private _isGrantor!: boolean;
    private _grantorAccount!: Account;
    // Contacts et adresse
    private _address!: Address;
    private _contacts!: PartnerContact[];
    // Fiscalité
    private _taxExempt!: boolean;
    private _taxExemptionDetail!: TaxDetail;
    // Automatisations
    private _identifiers!: PartnerIdentifier[];
    private _invoicesAutoMatching!: boolean;
    // Enrichissement IA
    private _businessContext!: string;
    private _businessContextSearchDone!: boolean;
    private _omitBusinessContext!: boolean;
    // Pour clients de Biifor
    private _subscription!: Subscription;

    constructor(data: any) {
        super(data);
        this._contacts = this._contacts || [];
        this._identifiers = this._identifiers || [];
        this._chargeAccountSuggestions = this._chargeAccountSuggestions || [];
        this._clientAccountSuggestions = this._clientAccountSuggestions || [];
        this._productAccountSuggestions = this._productAccountSuggestions || [];
        this._vendorAccountSuggestions = this._vendorAccountSuggestions || [];
    }

    public hasReferential(): boolean {
    return this.isOrganization && (
            this.isCaledonian ||
            this.isFrench ||
            this.isPolynesian
        );
    }

    public override toString(): string {
        return this._name;
    }

    public get isCaledonian(): boolean {
        return this._country?.alpha2 === 'NC';
    }

    public get isFrench(): boolean {
        return this._country?.alpha2 === 'FR';
    }

    public get isOrganization(): boolean {
        return this.type?.code === 'organization';
    }

    public get isPolynesian(): boolean {
        return this._country?.alpha2 === 'PF';
    }

    public get primaryContact(): PartnerContact | undefined {
        return this._contacts.find(c => c.primary);
    }

    public get remainingBalance(): number {
        return this.clientAccount?.remainingInitialBalance || 0 + this.vendorAccount?.remainingInitialBalance || 0;
    }

    public get address(): Address {
        return this._address;
    }

    public set address(value: any) {
        this._setValue('_address', value, 'Address');
    }

    public get autoChargeAccount(): boolean {
        return this._autoChargeAccount;
    }

    public set autoChargeAccount(value: any) {
        this._setValue('_autoChargeAccount', value, 'boolean');
    }

    public get autoProductAccount(): boolean {
        return this._autoProductAccount;
    }

    public set autoProductAccount(value: any) {
        this._setValue('_autoProductAccount', value, 'boolean');
    }

    public get businessContext(): string {
        return this._businessContext;
    }

    public set businessContext(value: any) {
        this._setValue('_businessContext', value, 'string');
    }

    public get businessContextSearchDone(): boolean {
        return this._businessContextSearchDone;
    }

    public set businessContextSearchDone(value: any) {
        this._setValue('_businessContextSearchDone', value, 'boolean');
    }

    public get chargeAccount(): Account {
        return this._chargeAccount;
    }

    public set chargeAccount(value: any) {
        this._setValue('_chargeAccount', value, 'Account');
    }

    /**
     * Refus explicite de la proposition d'enregistrement du compte de charge imputé
     * comme compte par défaut du fournisseur (bandeau de la section Imputations).
     * À distinguer d'un compte de charge déjà renseigné : les deux masquent le bandeau
     * mais pour des raisons opposées.
     */
    public get chargeAccountSuggestionDismissed(): boolean {
        return this._chargeAccountSuggestionDismissed;
    }

    public set chargeAccountSuggestionDismissed(value: any) {
        this._setValue('_chargeAccountSuggestionDismissed', value, 'boolean');
    }

    public get chargeAccountSuggestions(): Account[] {
        return this._chargeAccountSuggestions;
    }

    public set chargeAccountSuggestions(value: any) {
        this._setValue('_chargeAccountSuggestions', value, 'Account');
    }

    public get clientAccount(): Account {
        return this._clientAccount;
    }

    public set clientAccount(value: any) {
        this._setValue('_clientAccount', value, 'Account');
    }

    public get clientAccountSuggestions(): Account[] {
        return this._clientAccountSuggestions;
    }

    public set clientAccountSuggestions(value: any) {
        this._setValue('_clientAccountSuggestions', value, 'Account');
    }

    public get clientBalance(): number {
        return this._clientBalance;
    }

    public set clientBalance(value: any) {
        this._setValue('_clientBalance', value, 'float');
    }

    public get clientCreditorAccount(): Account {
        return this._clientCreditorAccount;
    }

    public set clientCreditorAccount(value: any) {
        this._setValue('_clientCreditorAccount', value, 'Account');
    }

    public get clientGroup(): PartnerGroup {
        return this._clientGroup;
    }

    public set clientGroup(value: any) {
        this._setValue('_clientGroup', value, 'PartnerGroup');
    }

    public get clientInitialBalance(): number {
        return this._clientInitialBalance;
    }

    public set clientInitialBalance(value: any) {
        this._setValue('_clientInitialBalance', value, 'float');
    }

    public get clientNumber(): string {
        return this._clientNumber;
    }

    public set clientNumber(value: any) {
        this._setValue('_clientNumber', value, 'string');
    }

    public get clientRemainingInitialBalance(): number {
        return this._clientRemainingInitialBalance;
    }

    public set clientRemainingInitialBalance(value: any) {
        this._setValue('_clientRemainingInitialBalance', value, 'float');
    }

    public get contacts(): PartnerContact[] {
        return this._contacts;
    }

    public set contacts(value: any) {
        this._setValue('_contacts', value, 'PartnerContact');
    }

    public get country(): Country {
        return this._country;
    }

    public set country(value: any) {
        this._setValue('_country', value, 'Country');
    }

    public get grantorAccount(): Account {
        return this._grantorAccount;
    }

    public set grantorAccount(value: any) {
        this._setValue('_grantorAccount', value, 'Account');
    }

    public get identifiers(): PartnerIdentifier[] {
        return this._identifiers;
    }

    public set identifiers(value: any) {
        this._setValue('_identifiers', value, 'PartnerIdentifier');
    }

    public get invoicesAutoMatching(): boolean {
        return this._invoicesAutoMatching;
    }

    public set invoicesAutoMatching(value: any) {
        this._setValue('_invoicesAutoMatching', value, 'boolean');
    }

    public get isClient(): boolean {
        return this._isClient;
    }

    public set isClient(value: any) {
        this._setValue('_isClient', value, 'boolean');
    }

    public get isVendor(): boolean {
        return this._isVendor;
    }

    public set isVendor(value: any) {
        this._setValue('_isVendor', value, 'boolean');
    }

    public get isGrantor(): boolean {
        return this._isGrantor;
    }

    public set isGrantor(value: any) {
        this._setValue('_isGrantor', value, 'boolean');
    }

    public get myVendorNumber(): string {
        return this._myVendorNumber;
    }

    public set myVendorNumber(value: any) {
        this._setValue('_myVendorNumber', value, 'string');
    }

    public get name(): string {
        return this._name;
    }

    public set name(value: any) {
        this._setValue('_name', value, 'string');
    }

    /**
     * Indicateur front, jamais envoyé : demande à `PartnerService` de retirer `businessContext`
     * du payload. Posé par l'écran qui n'affiche pas le contexte — la valeur que porte la fiche
     * est celle de la dernière lecture, la recherche en arrière-plan a pu l'écrire depuis (#1267).
     */
    @OmitField()
    public get omitBusinessContext(): boolean {
        return !!this._omitBusinessContext;
    }

    public set omitBusinessContext(value: any) {
        this._setValue('_omitBusinessContext', value, 'boolean');
    }

    public get organization(): Organization {
        return this._organization;
    }

    public set organization(value: any) {
        this._setValue('_organization', value, 'Organization');
    }

    public get productAccount(): Account {
        return this._productAccount;
    }

    public set productAccount(value: any) {
        this._setValue('_productAccount', value, 'Account');
    }

    /**
     * Pendant de {@link chargeAccountSuggestionDismissed} côté ventes : refus explicite
     * de la proposition d'enregistrement du compte de produit imputé comme compte
     * par défaut du client.
     */
    public get productAccountSuggestionDismissed(): boolean {
        return this._productAccountSuggestionDismissed;
    }

    public set productAccountSuggestionDismissed(value: any) {
        this._setValue('_productAccountSuggestionDismissed', value, 'boolean');
    }

    public get productAccountSuggestions(): Account[] {
        return this._productAccountSuggestions;
    }

    public set productAccountSuggestions(value: any) {
        this._setValue('_productAccountSuggestions', value, 'Account');
    }

    /**
     * Réglages de vente propres au client (#1114) : sous-objet de la fiche, servi masqué
     * en liste et à demander par `$expand=salesSettings/*` sur la fiche. Il porte aussi la
     * devise, le délai de paiement et l'acceptation des avenants, qui vivaient à la racine.
     */
    public get salesSettings(): PartnerSalesSettings | undefined {
        return this._salesSettings;
    }

    public set salesSettings(value: any) {
        this._setValue('_salesSettings', value, 'PartnerSalesSettings');
    }

    public get subscription(): Subscription {
        return this._subscription;
    }

    public set subscription(value: any) {
        this._setValue('_subscription', value, 'Subscription');
    }

    public get taxExempt(): boolean {
        return this._taxExempt;
    }

    public set taxExempt(value: any) {
        this._setValue('_taxExempt', value, 'boolean');
    }

    /**
     * Motif de l'exonération **individuelle** du tiers — #1089.
     *
     * Il ne qualifie que l'exonération attestée (`taxExempt`), jamais celle qui tient
     * au territoire : une vente hors territoire tire son motif de l'opération, et le
     * serveur le pose lui-même. La liste offerte est donc filtrée sur
     * `TaxDetail.partnerEligible`.
     */
    public get taxExemptionDetail(): TaxDetail {
        return this._taxExemptionDetail;
    }

    public set taxExemptionDetail(value: any) {
        this._setValue('_taxExemptionDetail', value, 'TaxDetail');
    }

    public set type(value: any) {
        this._setValue('_type', value, 'PartnerType');
    }

    public get type(): PartnerType {
        return this._type;
    }

    public get vendorAccount(): Account {
        return this._vendorAccount;
    }

    public set vendorAccount(value: any) {
        this._setValue('_vendorAccount', value, 'Account');
    }

    public get vendorAccountSuggestions(): Account[] {
        return this._vendorAccountSuggestions;
    }

    public set vendorAccountSuggestions(value: any) {
        this._setValue('_vendorAccountSuggestions', value, 'Account');
    }

    public get vendorBalance(): number {
        return this._vendorBalance;
    }

    public set vendorBalance(value: any) {
        this._setValue('_vendorBalance', value, 'float');
    }

    public get vendorCurrency(): Currency {
        return this._vendorCurrency;
    }

    public set vendorCurrency(value: any) {
        this._setValue('_vendorCurrency', value, 'Currency');
    }

    public get vendorDebtorAccount(): Account {
        return this._vendorDebtorAccount;
    }

    public set vendorDebtorAccount(value: any) {
        this._setValue('_vendorDebtorAccount', value, 'Account');
    }

    public get vendorGroup(): PartnerGroup {
        return this._vendorGroup;
    }

    public set vendorGroup(value: any) {
        this._setValue('_vendorGroup', value, 'PartnerGroup');
    }

    public get vendorInitialBalance(): number {
        return this._vendorInitialBalance;
    }

    public set vendorInitialBalance(value: any) {
        this._setValue('_vendorInitialBalance', value, 'float');
    }

    public get vendorInvoiceAutoValidation(): boolean {
        return this._vendorInvoiceAutoValidation;
    }

    public set vendorInvoiceAutoValidation(value: any) {
        this._setValue('_vendorInvoiceAutoValidation', value, 'boolean');
    }

    public get vendorNumber(): string {
        return this._vendorNumber;
    }

    public set vendorNumber(value: any) {
        this._setValue('_vendorNumber', value, 'string');
    }

    public get vendorPaymentTerm(): PaymentTerm | undefined {
        return this._vendorPaymentTerm;
    }

    public set vendorPaymentTerm(value: any) {
        this._setValue('_vendorPaymentTerm', value, 'PaymentTerm');
    }

    public get vendorRemainingInitialBalance(): number {
        return this._vendorRemainingInitialBalance;
    }

    public set vendorRemainingInitialBalance(value: any) {
        this._setValue('_vendorRemainingInitialBalance', value, 'float');
    }
}
